import React, {useEffect, useRef, useState} from 'react'
import axios from 'axios'
import styled, {keyframes} from 'styled-components'
import {Html5Qrcode, Html5QrcodeSupportedFormats} from 'html5-qrcode'
import {Apis} from "../util/Apis"
import {useLocation, useNavigate} from "react-router"
import success_icon from '../components/icons/success_icon.svg'
import error_icon from '../components/icons/error_icon.svg'
import {ButtonError, ButtonSuccess} from '../components/generales/customButton'
import {MdQrCodeScanner} from 'react-icons/md'
import {Card, CardHeader, PageWrapper} from '../components/blocks'
import {Modal} from '../components/atoms'

const customAnimation = keyframes`
 0% {
    -webkit-transform: rotate(-360deg) scale(0);
    transform: rotate(-360deg) scale(0);
    opacity: 0;
  }
  100% {
    -webkit-transform: rotate(0) scale(1);
    transform: rotate(0) scale(1);
    opacity: 1;
  }
`

const ShowOnloadIcon = styled.img`
  width: 120px;
  margin-bottom: 1rem;
  -webkit-animation: ${customAnimation} 0.8s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
  animation: ${customAnimation} 0.8s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
`

const ModalTitle = styled.span`
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
`

const ModalMessage = styled.span`
  margin-bottom: 2rem;
  text-align: center;
`

const ContainerErrorMessage = styled.div`
  margin-bottom: 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ScannerViewport = styled.div`
  position: relative;
  width: 100%;
  min-height: 320px;
  margin-top: 1rem;
  overflow: hidden;
  border-radius: 1rem;
  background: #111827;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ScannerRegion = styled.div`
  width: 100%;
  min-height: 320px;
`

const ScannerOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  text-align: center;
  color: #ffffff;
  background: rgba(17, 24, 39, 0.55);
`

const ScannerStatus = styled.span`
  text-align: center;
  color: #4b5563;
`

const DEFAULT_SCANNER_STATUS = 'Alinea el QR dentro del recuadro.'
const SCANNER_ZOOM = 1.5

function useQuery() {
  const {search} = useLocation()
  return React.useMemo(() => new URLSearchParams(search), [search])
}

const scoreCamera = (camera) => {
  const label = (camera.label || '').toLowerCase()
  let score = 0

  if (/camera\s*0/.test(label)) score += 60
  if (/(back|rear|environment|trasera|trase|posterior)/.test(label)) score += 100
  if (/(wide|main|principal|primary)/.test(label)) score += 20

  return score
}

const pickPreferredCamera = (cameras) => {
  if (!cameras.length) return null

  const exactMatch = cameras.find((camera) => {
    const label = (camera.label || '').toLowerCase()
    return /camera\s*0/.test(label) && /(back|rear|environment|trasera|trase|posterior)/.test(label)
  })

  if (exactMatch) return exactMatch
  return [...cameras].sort((left, right) => scoreCamera(right) - scoreCamera(left))[0]
}

const getQrBox = (viewfinderWidth, viewfinderHeight) => ({
  width: Math.max(50, Math.min(Math.floor(viewfinderWidth * 0.8), 280)),
  height: Math.max(50, Math.min(Math.floor(viewfinderHeight * 0.5), 280)),
})

const extractQrCodeValue = (value) => {
  const rawValue = `${value || ''}`.trim()
  if (!rawValue) return ''

  try {
    const url = new URL(rawValue)
    const segmentIndex = url.pathname.indexOf('/s/')
    if (segmentIndex >= 0) {
      return url.pathname.slice(segmentIndex + 3).replace(/^\/+|\/+$/g, '')
    }
  } catch (error) {
    // Fallback to regex parsing for non-URL payloads.
  }

  const pathMatch = rawValue.match(/\/s\/([^/?#]+)/i)
  if (pathMatch) return pathMatch[1]

  return rawValue.length > 26 ? rawValue.substring(26) : rawValue
}

const applyPreferredCameraSettings = async (scanner) => {
  const cameraCapabilities = scanner.getRunningTrackCameraCapabilities()
  const zoomFeature = cameraCapabilities.zoomFeature()
  if (zoomFeature.isSupported()) {
    const boundedZoom = Math.min(
      zoomFeature.max(),
      Math.max(zoomFeature.min(), SCANNER_ZOOM)
    )
    await zoomFeature.apply(boundedZoom).catch(() => undefined)
  }

  const trackCapabilities = scanner.getRunningTrackCapabilities()
  if (Array.isArray(trackCapabilities.focusMode)) {
    if (trackCapabilities.focusMode.includes('continuous')) {
      await scanner.applyVideoConstraints({
        advanced: [{focusMode: 'continuous'}]
      }).catch(() => undefined)
    } else if (trackCapabilities.focusMode.includes('single-shot')) {
      await scanner.applyVideoConstraints({
        advanced: [{focusMode: 'single-shot'}]
      }).catch(() => undefined)
    }
  }
}

const LectorQR = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [error2, setError2] = useState(null)
  const [show, setShow] = useState(false)
  const [scannerStarting, setScannerStarting] = useState(false)
  const [scannerStatus, setScannerStatus] = useState(DEFAULT_SCANNER_STATUS)
  const query = useQuery()
  const salaInfo = JSON.parse(localStorage.getItem('salaInfo'))
  const [showModalSuccess, setShowModalSuccess] = useState(false)
  const [showModalError, setShowModalError] = useState(false)
  const scannerRef = useRef(null)
  const scannerRegionIdRef = useRef(`qr-reader-${Math.random().toString(36).slice(2)}`)
  const handledDetectionRef = useRef(false)
  const loadingRef = useRef(false)
  const checkQrRef = useRef(() => {})

  useEffect(() => {
    const code = query.get('code')
    if (code !== null && code !== '') {
      checkQr(code)
      setShow(false)
    } else {
      setShow(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const stopScanner = async () => {
    handledDetectionRef.current = false
    const scanner = scannerRef.current
    scannerRef.current = null
    if (!scanner) return

    try {
      if (scanner.isScanning) {
        await scanner.stop()
      }
    } catch (error) {
      // Best effort cleanup.
    }

    try {
      await scanner.clear()
    } catch (error) {
      // Container may already be cleared.
    }
  }

  const checkQr = (code) => {
    if (!code || code.length === 0) {
      return
    }

    setLoading(true)
    setError(null)
    setError2(null)

    const body = {code: code, salaId: salaInfo.salaId}

    axios.post(Apis.CAPP + '/qr-login', body).then(() => {
      console.log('logged successfully')
      setError(null)
      setLoading(false)
      successScan()
    }).catch((err) => {
      setLoading(false)
      handleError(err, code)
      errorScan()
    })
  }

  checkQrRef.current = checkQr

  useEffect(() => {
    loadingRef.current = loading
  }, [loading])

  const handleError = (err, data) => {
    if (err.response) {
      setError(err.response.data.message + ' | ' + data)
    } else {
      setError('Hubo un error')
    }
  }

  function successScan() {
    setShowModalError(false)
    setShowModalSuccess(true)

    const timer = setTimeout(() => {
      navigate('/home')
    }, 3000)
    return () => clearTimeout(timer)
  }

  function errorScan() {
    setShowModalError(true)
  }

  function goHome() {
    setShowModalSuccess(false)
    navigate('/home')
  }

  function handleCloseError() {
    setShowModalError(false)
    navigate('/resumen')
  }

  useEffect(() => {
    if (!show) return undefined

    let cancelled = false

    const startScanner = async () => {
      setScannerStarting(true)
      setScannerStatus('Abriendo camara...')
      setError2(null)

      try {
        const scanner = new Html5Qrcode(scannerRegionIdRef.current, {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          verbose: false,
        })

        scannerRef.current = scanner

        const cameras = await Html5Qrcode.getCameras()
        if (cancelled) return

        const camera = pickPreferredCamera(cameras)
        if (!camera) {
          throw new Error('No se encontro ninguna camara disponible.')
        }

        setScannerStatus(`Usando ${camera.label || 'camara trasera'}...`)

        await scanner.start(
          camera.id,
          {
            fps: 10,
            qrbox: getQrBox,
            aspectRatio: 1,
            disableFlip: false,
          },
          async (decodedText) => {
            if (cancelled || loadingRef.current || handledDetectionRef.current) return

            const code = extractQrCodeValue(decodedText)
            if (!code) return

            handledDetectionRef.current = true
            setScannerStarting(false)
            setScannerStatus(DEFAULT_SCANNER_STATUS)
            await stopScanner()
            if (cancelled) return
            checkQrRef.current(code)
          },
          () => undefined
        )

        if (cancelled) return

        await applyPreferredCameraSettings(scanner)
        if (cancelled) return

        setScannerStarting(false)
        setScannerStatus('Buscando QR...')
      } catch (scannerError) {
        if (cancelled) return

        const message = scannerError instanceof Error && scannerError.message
          ? scannerError.message
          : 'No se pudo iniciar la camara.'

        setScannerStarting(false)
        setScannerStatus('')
        setError2(message)
        errorScan()
        await stopScanner()
      }
    }

    void startScanner()

    return () => {
      cancelled = true
      void stopScanner()
    }
  }, [show])

  return (
    <PageWrapper>
      {show && (
        <Card>
          <CardHeader
            title="Lector de QR"
            subtitle="Escanea el codigo QR de la maquina y juega"
            icon={<MdQrCodeScanner size={24} />}
          />
          <div className="flex flex-col w-full gap-4">
            <ScannerViewport>
              <ScannerRegion id={scannerRegionIdRef.current} />
              {scannerStarting && (
                <ScannerOverlay>Abriendo camara...</ScannerOverlay>
              )}
            </ScannerViewport>
            <ScannerStatus>{scannerStatus}</ScannerStatus>
          </div>
        </Card>
      )}

      <Modal open={showModalSuccess} onClose={() => setShowModalSuccess(false)}>
        <div className="flex flex-col items-center">
          <ShowOnloadIcon src={success_icon} alt="success" />
          <ModalTitle>FELICIDADES</ModalTitle>
          <ModalMessage>Iniciaste sesion correctamente!</ModalMessage>
          <ButtonSuccess onClick={() => goHome()}>ACEPTAR</ButtonSuccess>
        </div>
      </Modal>
      <Modal open={showModalError} onClose={() => setShowModalError(false)}>
        <div className="flex flex-col items-center">
          <ShowOnloadIcon src={error_icon} alt="error" />
          <ModalTitle>
            {error && <span>{error}</span>}
            {error2 && <span>{error2}</span>}
          </ModalTitle>
          <ContainerErrorMessage>
            <span>Tuvimos problemas para iniciar sesion</span>
            <span>Vuelva a intentarlo!</span>
          </ContainerErrorMessage>

          <ButtonError onClick={handleCloseError}>ACEPTAR</ButtonError>
        </div>
      </Modal>
    </PageWrapper>
  )
}

export default LectorQR
